<script setup lang="ts">
import { resetFormToItem } from '@/helpers/form.helper';
import { ref, computed, toRef } from 'vue';
import { useQuasar } from 'quasar';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';

import type { Locales, DotDto, FormDto, DotVotingStepDto } from '@/services/types/api.types';
import { useFormsStore } from '@/stores';
import { getTranslation } from '@/composables/data-display-utils';
import { provideEditLocale } from '@/composables/data-display-utils';
import { useLanguageValidationErrors } from '@/composables';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import { useWatchItemLoading } from '@/composables/watch-item-loading';
import { dotVotingChoiceValidationSchema } from '@/validation/dotVotingChoiceSchema';

import ReorderableTable from '@/components/ReorderableTable.vue';
import DotVotingChoiceEditDialog from '@/components/dialogs/DotVotingChoiceEditDialog.vue';
import DotVotingConfiguration from '@/components/DotVotingConfiguration.vue';
import FormActions from '@/components/FormActions.vue';

// CONST
const $q = useQuasar();
const { t } = useI18n();

// STORE
const formsStore = useFormsStore();

// PROPS
const props = defineProps<{
  step: DotVotingStepDto;
  formId: number;
  permissions: {
    canChoicesBeDeleted?: boolean;
  };
  languageEnabled: Locales[];
}>();

const editLocale = provideEditLocale(toRef(() => props.languageEnabled));

// FORM
const validationSchema = computed(() => dotVotingChoiceValidationSchema(props.languageEnabled));
const formContext = useForm({ validationSchema });
const { handleSubmit, errors, meta, resetForm } = formContext;

const form = useWatchItemLoading<FormDto>(toRef(props, 'formId'), formsStore, (f) => resetFormToItem(formContext, f));
const languageErrors = useLanguageValidationErrors(errors);

const columns = ref([
  {
    name: 'title',
    label: t('form.dot-voting.fields.title'),
    align: 'left',
    field: (row: DotDto) => getTranslation(row, 'title', props.languageEnabled),
  },
  {
    name: 'image',
    label: t('form.dot-voting.fields.image'),
    align: 'left',
    field: (row: DotDto) => row.image?.url,
  },
  {
    name: 'cost',
    label: t('form.dot-voting.fields.cost'),
    align: 'left',
    field: (row: DotDto) => row.cost,
  },
]);

const handleRowClick = (row: DotDto) => {
  $q.dialog({
    component: DotVotingChoiceEditDialog,
    componentProps: {
      mode: 'edit',
      dot: row,
      formId: props.formId,
      stepId: props.step.id,
      languageEnabled: props.languageEnabled,
      dotVotingType: props.step.dot_voting_type,
      dotVotingMeasurementType: props.step.dot_voting_measurement_type,
      defaultCostPerChoice: props.step.default_cost_per_choice,
      numberOfSteps: props.step.number_of_steps,
    },
  });
};

const handleAddRow = () => {
  const dotVotingChoice = formsStore.createDotVotingChoice();
  $q.dialog({
    component: DotVotingChoiceEditDialog,
    componentProps: {
      mode: 'add',
      dot: dotVotingChoice,
      formId: props.formId,
      stepId: props.step.id,
      languageEnabled: props.languageEnabled,
      dotVotingType: props.step.dot_voting_type,
      dotVotingMeasurementType: props.step.dot_voting_measurement_type,
      defaultCostPerChoice: props.step.default_cost_per_choice,
      numberOfSteps: props.step.number_of_steps,
    },
  });
};

const handleDeleteRow = (row: DotDto) => {
  $q.dialog({
    title: t('form.dialogs.dot-voting.delete-title'),
    message: t('form.dialogs.dot-voting.delete-message'),
    ok: {
      label: t('global.yes'),
      color: 'negative',
      flat: true,
    },
    cancel: {
      label: t('global.no'),
      color: 'primary',
      unelevated: true,
    },
  }).onOk(() => {
    if (!props.step.id) return;
    formsStore.deleteDotRow(row, props.formId, props.step.id);
  });
};

const handleReorder = async (source: DotDto, target: DotDto) => {
  if (!props.step.id) return;

  await formsStore.reorderDotVotingChoice(source, props.formId, props.step.id);
  await formsStore.reorderDotVotingChoice(target, props.formId, props.step.id);
};

const onCancel = () => {
  if (form.value) {
    resetForm({ values: form.value });
  } else {
    console.warn('onCancel: form is null');
  }
};

const onSubmit = handleSubmit(async (values) => {
  console.debug('submitting', values);
  wrapSavingOperation(
    async () => await formsStore.saveForm(values as FormDto),
    () => resetForm({ values: form.value! }),
  );
});
</script>

<template>
  <div class="dot-vote-choice">
    <form-actions
      v-model:editLocale="editLocale"
      :onCancel="onCancel"
      :onSubmit="onSubmit"
      :languageErrors="languageErrors"
      :meta="meta"
      :language-enabled="languageEnabled"
    />
    <dot-voting-configuration :language-enabled="languageEnabled" :step-index="0" />
    <q-separator />
    <reorderable-table
      :rows="[...(props.step.dots ?? [])].sort((a, b) => a.order_num - b.order_num)"
      :columns="columns"
      :title="$t('form.dot-voting.list-title')"
      itemKey="id"
      :disable-delete-button="true"
      :action-menu-items="[
        {
          action: 'delete-row',
          label: t('global.label.delete'),
          disabled: !props.permissions.canChoicesBeDeleted,
          tooltip: !props.permissions.canChoicesBeDeleted
            ? t('global.notifications.choice-prohibit-delete')
            : undefined,
        },
      ]"
      @rowClick="handleRowClick"
      @add-row="handleAddRow"
      @delete-row="handleDeleteRow"
      @reorder="handleReorder"
    >
      <template v-slot:body-cell-image="props">
        <q-td :props="props">
          <img :src="props.row.image?.url" style="max-height: 48px" />
        </q-td>
      </template>
    </reorderable-table>
  </div>
</template>
<style lang="scss">
.dot-vote-choice {
  .q-separator {
    margin: 16px 0;
  }
}
</style>
